import { useCallback, useState } from 'react'
import { barClearance } from '../../ui/bar-clearance'

/**
 * A request to go somewhere, which REPEATS.
 *
 * "Where the reader is" and "take the reader there" look like one fact and are
 * not. Held as a plain id, asking twice for the same place is not a second ask —
 * the state never changes, so nothing runs, and a control that navigates works
 * once and is dead every time after, looking identical either way.
 *
 * So the ask is ONE value carrying a count, rather than an id beside a counter.
 * An effect reads the whole thing and depends on the whole thing: the identity
 * moves on every ask, which is what makes it a trigger, and the id stays
 * readable for whatever marks the position.
 */
export type GoTo = { id: string | null; n: number }

/**
 * How far a reader's eye is willing to travel. Beyond it, the distance is jumped
 * rather than shown.
 */
const TRAVEL = 420

/**
 * Arrive somewhere with a short scroll, however far away it was.
 *
 * A smooth scroll across a whole book is not a scroll a reader follows — it is
 * the page flying past for a second while nothing can be read, and at the end of
 * it they have lost where they came from as well. The distance carries no
 * information, so it is jumped: the near approach is what says "this moved, and
 * here is where it went", and that part is worth animating.
 *
 * The jump lands on the side the reader is coming FROM, so the glide continues
 * in the direction the eye already expects rather than doubling back.
 *
 * Written as a read and a write so one function serves the window and a panel;
 * the caller knows which it is scrolling and this does not have to.
 */
export function glide(
  read: () => number,
  write: (top: number, smooth: boolean) => void,
  to: number,
) {
  const from = read()
  if (Math.abs(to - from) > TRAVEL) {
    write(to + (to > from ? -TRAVEL : TRAVEL), false)
  }
  /* After the jump has been applied, or the browser coalesces the two and only
     the second is seen — which is the long scroll this exists to avoid. */
  requestAnimationFrame(() => write(to, true))
}

/**
 * Bring an element to the top of the WINDOW, the same way anything else in this
 * reader arrives: the distance jumped, the last stretch glided.
 *
 * `scrollIntoView` cannot do it — it decides the whole journey itself, so a
 * contents entry twenty chapters away either lands with no sense of having
 * moved or animates the entire book past the reader. Here every arrival in the
 * book feels the same however far it came from, which is what makes the motion
 * mean "it went there" rather than "something happened".
 *
 * It comes to rest BELOW the fixed bar rather than under it. The bar is over
 * the window at a height the shell owns, so a target scrolled to the window's
 * own top is a target behind the chrome — and the reader, having asked to be
 * taken somewhere, arrives at the one part of the page they cannot see. The
 * distance is read from `--topbar-h`, which is what every sticky thing in the
 * app already offsets from, so nothing here holds a second copy of it.
 */
export function glideToElement(el: Element | null, clear = barClearance()) {
  if (!el) return
  const to = Math.max(
    0,
    window.scrollY + el.getBoundingClientRect().top - clear,
  )
  glide(
    () => window.scrollY,
    (top, smooth) =>
      window.scrollTo({ top, behavior: smooth ? 'smooth' : 'auto' }),
    to,
  )
}

export function useGoTo() {
  const [asked, setAsked] = useState<GoTo>({ id: null, n: 0 })
  const go = useCallback(
    (id: string | null) => setAsked((held) => ({ id, n: held.n + 1 })),
    [],
  )
  return [asked, go] as const
}
